import { BrowserModule, TransferState } from '@angular/platform-browser';
import { NgModule, Injector, LOCALE_ID, APP_INITIALIZER } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { QuillModule } from 'ngx-quill'

import localePl from '@angular/common/locales/pl';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule, HttpClient } from '@angular/common/http';

import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateModule, TranslateService, TranslateLoader } from '@ngx-translate/core';
import { AppInitializerFactory } from './lang/AppInitializerFactory';


import { AppComponent } from './app.component';
import { SharedModule } from './shared/shared.module';
import { CoreModule } from './core/core.module';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

/* pages */
import { RegulationsComponent } from './pages/regulations/regulations.component';
import { AboutUsComponent } from './pages/about-us/about-us.component';
import { PaymentsMethodsComponent } from './pages/payments-methods/payments-methods.component';
import { HowBuyComponent } from './pages/how-buy/how-buy.component';
import { ContactComponent } from './pages/contact/contact.component';
import { PassportInterceptor } from './core/interceptor/passport.interceptor';
import { LoginComponent } from './pages/auth/login/login.component';
import { RegisterPageComponent } from './pages/auth/register-page/register-page.component';
import { ForgotPasswordPageComponent } from './pages/auth/forgot-password-page/forgot-password-page.component';
import { MainPageComponent } from './pages/main-page/main-page.component';
import { SearchComponent } from './pages/search/search.component';
import { UserAccountComponent } from './pages/user-account/userAccount.component';
import { UserDetailsComponent } from './pages/user-account/user-details/userDetails.component';
import { ChangeEmailComponent } from './pages/user-account/change-email/changeEmail.component';
import { ChangePasswordComponent } from './pages/user-account/change-password/changePassword.component';
import { MyOrdersComponent } from './pages/user-account/my-orders/myOrders.component';
import { PrepaidComponent } from './pages/prepaid/prepaid.component';
import { TransactionCompletedComponent } from './pages/transaction-completed/transaction-completed.component';
import { AdminPanelComponent } from './pages/admin-panel/admin-panel.component';
import { UsersComponent } from './pages/admin-panel/users/users.component';
import { ArrangerDetailsComponent } from './pages/user-account/arranger-details/arranger-details.component';
import { UserFullDetailsComponent } from './pages/admin-panel/user-full-details/user-full-details.component';
import { UserAuditHistoryComponent } from './pages/admin-panel/user-full-details/user-audit-history/user-audit-history.component';
import { CategoryManagementComponent } from './pages/admin/category-management/category-management.component';
import { BrowserStorageService } from './services/storage.service';
import { DataExportComponent } from './pages/admin-panel/data-export/data-export.component';
import { NotePageComponent } from './pages/note-page/note-page.component';
import { InformationPageEditorComponent } from './pages/admin-panel/information-pages/information-page-editor/information-page-editor.component';
import { InformationPagesComponent } from './pages/admin-panel/information-pages/information-pages.component';
import { InvoiceDataComponent } from './pages/admin-panel/invoice-data/invoice-data.component';
import { CartPageComponent } from './pages/cart-page/cart-page.component';
import { AddPhotoComponent } from './pages/user-account/add-photo/add-photo.component';
import { PrepaidsComponent } from './pages/admin-panel/prepaids/prepaids.component';
import { ContractDataComponent } from './pages/admin-panel/contract-data/contract-data.component';
import { ErrorInterceptor } from './core/interceptor/error.interceptor';
import { OrderManagementComponent } from './pages/admin-panel/order-management/order-management.component';
import { ZaiksReportComponent } from './pages/admin-panel/zaiks-report/zaiks-report.component';
import { DeleteAccountComponent } from './pages/user-account/delete-account/delete-account.component';
import { AuthGuard } from './guard/auth.guard';
import { InvoiceDetailsComponent } from './pages/user-account/invoice-details/invoice-details.component';
import { ProfitComponent } from './pages/arranger/profit/profit.component';
import { InvoiceManagementComponent } from './pages/admin-panel/invoice-management/invoice-management.component';
import { NotesDownloadComponent } from './pages/admin-panel/notes-download/notes-download.component';
import { translateBrowserLoaderFactory } from './loaders/translate-browser.loader';
import { SongSuggestionPageComponent } from './pages/song-suggestion-page/song-suggestion-page.component';
import { PrivacyComponent } from './pages/privacy/privacy.component';
import { NewsletterUnsubscribePageComponent } from './pages/newsletter/newsletter-unsubscribe-page/newsletter-unsubscribe-page.component';
import { NewsletterComponent } from './pages/admin-panel/newsletter/newsletter.component';
import { NewsletterCreateComponent } from './pages/admin-panel/newsletter-create/newsletter-create.component';
import { NewsletterSendListComponent } from './pages/admin-panel/newsletter-send-list/newsletter-send-list.component';
import { PrepaidInvoicesComponent } from './pages/admin-panel/prepaid-invoices/prepaid-invoices.component';


registerLocaleData(localePl);
export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient);
}

@NgModule({
  declarations: [
    AppComponent,
    RegulationsComponent,
    AboutUsComponent,
    ContactComponent,
    PaymentsMethodsComponent,
    HowBuyComponent,
    LoginComponent,
    RegisterPageComponent,
    ForgotPasswordPageComponent,
    MainPageComponent,
    SearchComponent,
    UserAccountComponent,
    UserDetailsComponent,
    ChangePasswordComponent,
    ChangeEmailComponent,
    MyOrdersComponent,
    PrepaidComponent,
    TransactionCompletedComponent,
    AdminPanelComponent,
    UsersComponent,
    ArrangerDetailsComponent,
    UserFullDetailsComponent,
    UserAuditHistoryComponent,
    CategoryManagementComponent,
    DataExportComponent,
    NotePageComponent,
    InformationPageEditorComponent,
    InformationPagesComponent,
    CartPageComponent,
    AddPhotoComponent,
    InvoiceDataComponent,
    PrepaidsComponent,
    ContractDataComponent,
    OrderManagementComponent,
    ZaiksReportComponent,
    DeleteAccountComponent,
    InvoiceDetailsComponent,
    ProfitComponent,
    InvoiceManagementComponent,
    NotesDownloadComponent,
    SongSuggestionPageComponent,
    PrivacyComponent,
    NewsletterUnsubscribePageComponent,
    NewsletterComponent,
    NewsletterCreateComponent,
    NewsletterSendListComponent,
    PrepaidInvoicesComponent
  ],
  imports: [
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    QuillModule.forRoot(),
    CoreModule,
    HttpClientModule,
    SharedModule,
    BrowserAnimationsModule,
    FontAwesomeModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: translateBrowserLoaderFactory,
        deps: [HttpClient, TransferState]
      }
    }),
  ],
  providers: [
    TransferState,
    BrowserStorageService,
    {
      provide: APP_INITIALIZER,
      useFactory: AppInitializerFactory,
      deps: [TranslateService, Injector],
      multi: true
    },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: PassportInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: 'pl-PL' },
    AuthGuard
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }

